import React from "react";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import { computeDelta } from "../lib/dateDefaults";

/**
 * Tiny inline badge that shows the % change vs a prior period.
 * Renders nothing if prior data is missing.
 *
 * Props:
 *   current — number  (this period's value)
 *   prior   — number  (prior period's value)
 *   invert  — bool    (true for "lower is better" metrics like response time;
 *                      flips the colour scheme so down = green, up = red)
 *   label   — string  (optional caption, default "vs prev")
 */
const PeriodDelta = ({ current, prior, invert = false, label = "vs prev" }) => {
  // Loading: prior not yet fetched. Show a faint em-dash so users see the slot
  // is reserved (vs. mistakenly thinking comparison is unavailable).
  if (prior === undefined) {
    return (
      <span
        className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border text-slate-300 bg-slate-50 border-slate-100"
        data-testid="period-delta-badge"
        title="Comparing to previous period…"
        aria-label="Comparison loading"
      >—</span>
    );
  }
  if (prior === null) return null;
  const { pct, direction } = computeDelta(current, prior);
  if (pct === null) return null;

  const positive = invert ? direction === "down" : direction === "up";
  const negative = invert ? direction === "up" : direction === "down";
  const cls = positive
    ? "text-emerald-600 bg-emerald-50 border-emerald-200"
    : negative
    ? "text-rose-600 bg-rose-50 border-rose-200"
    : "text-slate-500 bg-slate-50 border-slate-200";

  const Icon = direction === "up" ? ArrowUp : direction === "down" ? ArrowDown : Minus;

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border ${cls}`}
      data-testid="period-delta-badge"
      title={`${pct > 0 ? "+" : ""}${pct}% ${label} (prior: ${prior})`}
    >
      <Icon className="w-2.5 h-2.5" />
      {pct > 0 ? "+" : ""}{pct}%
    </span>
  );
};

export default PeriodDelta;
